import React, { useEffect, useRef, useState } from 'react'
import { useInfiniteScroll, useRequest } from 'ahooks'
import Modal from 'meinian-package/src/component/modal'
import Empty from 'meinian-package/src/baseComponent/empty'
import request from 'meinian-package/src/utils/request'
import Loading from 'meinian-package/src/baseComponent/loading'
import { teamHasAuth } from 'meinian-package/src/utils/auth'
import isIPad from 'meinian-package/src/common/isIPad'
import Textbox from './textbox'
import Tag from './tag'
import css from './modal.module.less'
import Tab from './tab'
import Card from './card'
import SelectInspirations from './selectInspirations'

import { getMainUrl, type SearchType, useConfig, Params } from './util'

export type SearchModalProps = {
  inspirationId: string
  visible: boolean
  type: SearchType
  onCancel: () => void
}

const POST_URL = '/zhikuan/inspiration/search'
function showSampleTab(inspirationId: string) {
  if (!teamHasAuth('meinian_sample_clothing')) return false
  if (inspirationId) return false
  return true
}
function getSearchInputPlaceholder() {
  if (teamHasAuth('meinian_sample_clothing')) return '输入关键词搜索款式、样衣、灵感集、样衣文件夹'
  return '输入关键词搜索款式、灵感集'
}
const TAB_OPTIONS = [
  { label: '款式', value: 'blog', check: () => true },
  { label: '样衣', value: 'sample_clothing', check: showSampleTab },
  { label: '灵感集', value: 'inspiration', check: () => true },
  { label: '样衣文件夹', value: 'sample_folder', check: showSampleTab },
]
function initParams(type: SearchType, inspirationId: string) {
  return {
    searchType: type || 'blog',
    searchKey: '',
    pageSize: 20,
    start: 0,
    platformIdList: [],
    userIdList: [],
    searchRangeList: [],
    inspirationIdList: inspirationId ? [inspirationId] : null,
    queryType: 3,
  }
}
export default function SearchModal(props: SearchModalProps) {
  const { visible, onCancel, type, inspirationId } = props
  const $list = useRef(null)
  const [params, setParams] = useState<Params>(initParams(type, inspirationId))
  const config = useConfig(params.searchType)
  const { runAsync } = useRequest(
    async (d) => {
      if (!params.searchKey) {
        return {
          list: [],
          next: null,
        }
      }
      const start = d?.next || 0
      const _data = await request.post(POST_URL, { ...params, start })
      const result = (_data as any).resultList || []
      return {
        list: result,
        next: result.length > 0 ? start + params.pageSize : null,
      }
    },
    {
      debounceWait: 300,
      throttleWait: 200,
      manual: true,
    }
  )
  const { data, loading, loadingMore, reload, noMore } = useInfiniteScroll(runAsync, {
    target: $list.current,
    threshold: 100,
    manual: true,
    onError(e) {
      console.error(e)
    },
    isNoMore: (d) => d?.next === null,
  })

  const onTabChange = (value) => {
    const newValue: Params = {
      searchKey: params.searchKey,
      inspirationIdList: params.inspirationIdList,
      pageSize: 20,
      start: 0,
      platformIdList: [],
      userIdList: [],
      searchRangeList: [],
      queryType: 3,
      searchType: value as SearchType,
    }
    if (!config.canFilterInspiration) {
      newValue.inspirationIdList = null
    }
    setParams(newValue)
  }

  const onKeywordChange = (value) => {
    setParams({ ...params, searchKey: value, userIdList: [] })
  }

  const onInspirationChange = (value) => {
    if (value) {
      setParams({ ...params, inspirationIdList: [value] })
    } else {
      setParams({ ...params, inspirationIdList: null })
    }
  }

  const onRemoveInspiration = () => {
    setParams({ ...params, inspirationIdList: null })
  }
  useEffect(() => {
    if (visible) {
      setParams(initParams(type, inspirationId))
    }
  }, [visible])

  useEffect(() => {
    reload()
  }, [params])

  useEffect(() => {
    const keydown = (e) => {
      if (e.keyCode === 13 && visible) {
        reload()
      }
    }
    window.addEventListener('keydown', keydown)
    return () => {
      window.removeEventListener('keydown', keydown)
    }
  }, [visible])

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      hiddenFooter
      className={css.modal}
      title={
        <Textbox value={params.searchKey} placeholder={getSearchInputPlaceholder()} onChange={onKeywordChange}>
          {params.inspirationIdList && <Tag onRemove={() => onRemoveInspiration()} />}
        </Textbox>
      }
    >
      <div className={css.container}>
        <Tab
          className={css.tab}
          options={TAB_OPTIONS.filter((m) => m.check(params.inspirationIdList?.[0]))}
          value={params.searchType}
          onChange={onTabChange}
        >
          {config.canFilterInspiration && (
            <div className={css.insSelect}>
              <span className={css.mr3}>选择灵感集</span>
              <SelectInspirations value={params.inspirationIdList?.[0]} onChange={onInspirationChange} />
            </div>
          )}
        </Tab>
        <div className={css.content}>
          <div className={css.result} ref={$list}>
            {loading && <Loading spinning className={css.loading} />}
            <ul className={css.grid} style={{ gridTemplateColumns: `${isIPad ? '1fr 1fr' : '1fr 1fr 1fr 1fr'}` }}>
              {!loading
                && data?.list?.map((m) => (
                  <Card
                    key={m.id}
                    onClick={() => config.toDetail(m)}
                    image={getMainUrl(m)}
                    path={
                      params.searchType === 'blog' || params.searchType === 'sample_clothing' || m.parentId >= 0
                        ? m.namePath
                        : null
                    }
                    // 显示标题
                    title={params.searchType === 'inspiration' || params.searchType === 'sample_folder' ? m.name : ''}
                    subTitle={m.reason}
                  />
                ))}
            </ul>
            {!loading && data?.list.length === 0 && (
              <Empty
                title={params.searchKey ? '暂无相关搜索结果' : '输入关键字搜索'}
                image="https://zhiyi-image.oss-cn-hangzhou.aliyuncs.com/web/meinian/empty.png"
              />
            )}
            {loadingMore && <div className={css.noMore}>加载中……</div>}
            {!loading && noMore && data.list?.length > 0 && <div className={css.noMore}>没有更多内容了~</div>}
          </div>
          <div className={css.filter}>{config?.filters(params, (p) => setParams(p), data?.list ?? [])}</div>
        </div>
      </div>
    </Modal>
  )
}
